import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import {
  ActiveEmployeeRegistrationViewModel,
  EmployeeRegistrationDataViewModel,
  EmployeeRegistrationLoginDetails,
  EmployeeRegistrationShift,
  EmployeeRole,
  WebSocketClientService
} from 'chronos-core-client';
import { EmployeeRegistrationDsService } from '@app/core/data-services';
import { HubUrlsService } from '@app/core/utils';
import { map, tap } from 'rxjs/operators';
import { ListValue } from 'chronos-shared';
import { EmployeeService } from '@app/core/global-state/employees/employee.service';
import moment from 'moment-mini';

@Injectable()
export class EmployeeRegistrationDetailsService {
  public enableCancelSubject = new BehaviorSubject<boolean>(true);
  public enableCancelSubject$: Observable<boolean> = this.enableCancelSubject.asObservable();

  public setFocusSubject = new BehaviorSubject<boolean>(true);
  public setFocusSubject$: Observable<boolean> = this.setFocusSubject.asObservable();

  constructor(
    private employeeRegistrationDsService: EmployeeRegistrationDsService,
    private webSocketClientService: WebSocketClientService,
    private hubUrlsService: HubUrlsService,
    private employeeService: EmployeeService
  ) {}

  public getActiveLogin(workCenterId: number): Observable<ActiveEmployeeRegistrationViewModel> {
    return this.employeeRegistrationDsService.getActiveLogin(workCenterId);
  }

  public getLoginDetails(workCenterId: number, employeeId: number): Observable<EmployeeRegistrationLoginDetails> {
    return this.employeeRegistrationDsService.getLoginDetails(workCenterId, employeeId);
  }

  public getEmployeeListNotifications(workCenterId: number): Observable<EmployeeRegistrationDataViewModel> {
    return this.webSocketClientService.getNotificationsForTopic(this.hubUrlsService.getEmployeeRegistrationDetails(workCenterId)).pipe(
      tap((employeeRegistrationData: EmployeeRegistrationDataViewModel) => {
        this.employeeService.updateEmployeeRegistrationData(employeeRegistrationData);
      })
    );
  }

  public getEmployeeHistoryNotifications(workCenterId: number): Observable<any> {
    const employeeLoggedIn$ = this.webSocketClientService.getNotificationsForTopic(this.hubUrlsService.getEmployeeLoggedIn(workCenterId));
    const employeeLoggedOut$ = this.webSocketClientService.getNotificationsForTopic(this.hubUrlsService.getEmployeeLoggedOut(workCenterId));
    return merge(employeeLoggedIn$, employeeLoggedOut$);
  }

  public employeeLogin(
    employeeId: number,
    loginTime: string,
    note?: string,
    registrationPurpose?: string,
    role?: EmployeeRole,
    workCenterId?: number
  ): Observable<void> {
    return this.employeeRegistrationDsService.employeeLogin(employeeId, loginTime, note, registrationPurpose, role, workCenterId);
  }

  public logOut(employeeId: number, workCenterId: number): Observable<void> {
    return this.employeeRegistrationDsService.employeeLogout(employeeId, workCenterId);
  }

  public updateRegistration(loggedInEmployeeRegistration: ActiveEmployeeRegistrationViewModel, workCenterId: number): Observable<void> {
    return this.employeeRegistrationDsService.updateRegistration(
      loggedInEmployeeRegistration.id,
      loggedInEmployeeRegistration.note,
      loggedInEmployeeRegistration.role,
      workCenterId
    );
  }

  public getShiftsForFilter(workCenterId: number, inputShiftId: number, approvalReportDate: Date): Observable<ListValue[]> {
    if (inputShiftId > 0) {
      return this.employeeRegistrationDsService
        .getShiftsForFilterShiftReport(workCenterId, inputShiftId)
        .pipe(map((filter) => this.mapToListValue(filter)));
    } else if (approvalReportDate != null) {
      return this.employeeRegistrationDsService
        .getShiftsForFilterApproval(workCenterId, moment(approvalReportDate).format('YYYY-MM-DD'))
        .pipe(map((filter) => this.mapToListValue(filter)));
    } else {
      return this.employeeRegistrationDsService
        .getShiftsForFilter(workCenterId)
        .pipe(map((filter) => this.mapToListValue(filter)));
    }
  }

  private mapToListValue(shifts: EmployeeRegistrationShift[]): ListValue[] {
    return shifts.map(({ id, shiftTypeName, shiftStartTime }) => ({
      label: `${moment.utc(shiftStartTime).local().format('DD.MM.YYYY')} - ${shiftTypeName}`,
      value: id
    }));
  }
}
